import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import {
  DEFAULT_RISK_LAND_THRESHOLDS,
  RISK_LAND_THRESHOLD_META,
  mergeRiskLandThresholds,
  type RiskLandThresholdKey,
  type RiskLandThresholds,
} from '../../constants/riskLandThresholds';

type Props = {
  value: RiskLandThresholds;
  onChange: (next: RiskLandThresholds) => void;
  onSave?: () => void | Promise<void>;
  onResetDefaults?: () => void;
  saving?: boolean;
  /** 시설 폼: 시스템 기본값 표시용 */
  systemBase?: RiskLandThresholds | null;
  title?: string;
  description?: string;
  showActions?: boolean;
};

const PRIMARY_KEYS = (
  Object.keys(RISK_LAND_THRESHOLD_META) as RiskLandThresholdKey[]
).filter((k) => !RISK_LAND_THRESHOLD_META[k].advanced);

const ADVANCED_KEYS = (
  Object.keys(RISK_LAND_THRESHOLD_META) as RiskLandThresholdKey[]
).filter((k) => RISK_LAND_THRESHOLD_META[k].advanced);

const RiskLandThresholdsCard: React.FC<Props> = ({
  value,
  onChange,
  onSave,
  onResetDefaults,
  saving,
  systemBase,
  title = '위험 판정 임계값',
  description = '배회·특별관리구역·밀집 등 육상/시나리오 임계. 익사 motion(정지·민감도) 설정과는 별개.',
  showActions = true,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const base = useMemo(
    () => mergeRiskLandThresholds(systemBase || DEFAULT_RISK_LAND_THRESHOLDS),
    [systemBase]
  );

  const setKey = (key: RiskLandThresholdKey, raw: string) => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return;
    onChange({ ...value, [key]: n });
  };

  const renderField = (key: RiskLandThresholdKey) => {
    const meta = RISK_LAND_THRESHOLD_META[key];
    const inheritedHint =
      systemBase && Number(value[key]) === Number(base[key])
        ? `시스템 기본(${base[key]})과 동일`
        : systemBase
          ? `시스템 기본 ${base[key]}`
          : undefined;
    return (
      <Grid item xs={12} sm={6} md={4} key={key}>
        <TextField
          fullWidth
          type="number"
          label={meta.label}
          value={value[key]}
          onChange={(e) => setKey(key, e.target.value)}
          inputProps={{ min: meta.min, max: meta.max, step: meta.step }}
          helperText={inheritedHint ? `${meta.helper} · ${inheritedHint}` : meta.helper}
        />
      </Grid>
    );
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
        <Grid container spacing={2}>
          {PRIMARY_KEYS.map(renderField)}
        </Grid>
        <Box sx={{ mt: 1.5 }}>
          <Button size="small" onClick={() => setShowAdvanced((v) => !v)}>
            {showAdvanced ? '고급 숨기기' : '고급(미끄러짐·쓰러짐)'}
          </Button>
        </Box>
        <Collapse in={showAdvanced}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {ADVANCED_KEYS.map(renderField)}
          </Grid>
        </Collapse>
        {showActions && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {onSave && (
              <Button variant="contained" onClick={() => void onSave()} disabled={saving}>
                저장
              </Button>
            )}
            {onResetDefaults && (
              <Button
                variant="outlined"
                onClick={onResetDefaults}
                disabled={saving}
              >
                기본값으로
              </Button>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default RiskLandThresholdsCard;
